/**
 * クライアント視点の正方格子。同期しない。
 */

import type { Vec3 } from './types';
import { WORLD_LIMIT } from './validation';

export const VIEW_GRID_TILE = 1;
export const VIEW_GRID_WINDOW = 32;
export const SNAP_STEPS = [1, 0.5, 0.25] as const;
export type SnapStep = (typeof SNAP_STEPS)[number];
export const BLOCK_YAW_DEGREES = [0, 15, 30, 45, 60, 75] as const;
export const BLOCK_YAW_CLASS_COUNT = BLOCK_YAW_DEGREES.length;

export interface ViewGridState {
  y: number;
  yaw: number;
  visible: boolean;
  snap: number;
}

export interface GridCell {
  i: number;
  j: number;
}

export type GridSide = 'above' | 'below';

export const DEFAULT_VIEW_GRID: ViewGridState = {
  y: 0,
  yaw: 0,
  visible: true,
  snap: VIEW_GRID_TILE,
};

export function normalizeSnap(value: unknown, fallback: number = VIEW_GRID_TILE): number {
  return SNAP_STEPS.includes(value as SnapStep) ? Number(value) : fallback;
}

function gridSnap(grid: ViewGridState): number {
  return normalizeSnap(grid.snap);
}

export function yawRadians(yaw: number): number {
  return (yaw * Math.PI) / 180;
}

export function snapYawClass(yaw: number): number {
  if (!Number.isFinite(yaw)) return 0;
  const step = Math.round(yaw / 15);
  return ((step % BLOCK_YAW_CLASS_COUNT) + BLOCK_YAW_CLASS_COUNT) % BLOCK_YAW_CLASS_COUNT;
}

export function yawClassDegrees(yawClass: number): number {
  return BLOCK_YAW_DEGREES[yawClass] ?? 0;
}

/**
 * Three.js の `rotation.y = yaw` と同じ Y 回転。
 * 緑の線とブロックが同じ向きになる。
 */
export function worldToLocalXZ(x: number, z: number, yaw: number): { x: number; z: number } {
  const radians = yawRadians(yaw);
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return { x: x * cosine - z * sine, z: x * sine + z * cosine };
}

export function localToWorldXZ(x: number, z: number, yaw: number): { x: number; z: number } {
  const radians = yawRadians(yaw);
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return { x: x * cosine + z * sine, z: -x * sine + z * cosine };
}

/** 局所 +X が dir の XZ に重なる yaw（度）。 */
export function yawFromWorldXZ(x: number, z: number): number {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return 0;
  if (Math.hypot(x, z) < 1e-9) return 0;
  const deg = (Math.atan2(-z, x) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

export function worldToCell(point: Vec3, grid: ViewGridState): GridCell {
  const tile = gridSnap(grid);
  const local = worldToLocalXZ(point.x, point.z, grid.yaw);
  return {
    i: Math.floor(local.x / tile),
    j: Math.floor(local.z / tile),
  };
}

export function cellCenter(cell: GridCell, grid: ViewGridState): Vec3 {
  const tile = gridSnap(grid);
  const world = localToWorldXZ((cell.i + 0.5) * tile, (cell.j + 0.5) * tile, grid.yaw);
  return { x: world.x, y: grid.y, z: world.z };
}

/** XZ をマス中心へ。Y は呼び出し側の面の高さのまま。 */
export function snapToCellCenter(point: Vec3, grid: ViewGridState, cell?: GridCell): Vec3 {
  const center = cellCenter(cell ?? worldToCell(point, grid), grid);
  return { x: center.x, y: point.y, z: center.z };
}

export function cellCorners(cell: GridCell, grid: ViewGridState): Vec3[] {
  const tile = gridSnap(grid);
  const corners: Vec3[] = [];
  for (const [di, dj] of [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ] as const) {
    const world = localToWorldXZ((cell.i + di) * tile, (cell.j + dj) * tile, grid.yaw);
    corners.push({ x: world.x, y: grid.y, z: world.z });
  }
  return corners;
}

export function intersectGridPlane(
  origin: Vec3,
  direction: Vec3,
  grid: ViewGridState,
  maxDistance: number
): { point: Vec3; distance: number; side: GridSide; cell: GridCell } | null {
  if (!grid.visible) return null;
  if (Math.abs(direction.y) < 1e-8) return null;
  const distance = (grid.y - origin.y) / direction.y;
  if (distance < 0 || distance > maxDistance) return null;
  const point = {
    x: origin.x + direction.x * distance,
    y: grid.y,
    z: origin.z + direction.z * distance,
  };
  if (![point.x, point.z].every((value) => Math.abs(value) <= WORLD_LIMIT)) return null;
  return {
    point,
    distance,
    side: origin.y >= grid.y ? 'above' : 'below',
    cell: worldToCell(point, grid),
  };
}

/** 指定 yaw の正方格子へ XZ を揃え、Y は世界軸のまま丸める。 */
export function snapOnYaw(point: Vec3, yaw: number, snap: number): Vec3 {
  const step = snap > 0 ? snap : VIEW_GRID_TILE;
  const local = worldToLocalXZ(point.x, point.z, yaw);
  const world = localToWorldXZ(
    Math.round(local.x / step) * step,
    Math.round(local.z / step) * step,
    yaw
  );
  return { x: world.x, y: Math.round(point.y / step) * step, z: world.z };
}

export function snapOnGrid(point: Vec3, grid: ViewGridState): Vec3 {
  return { ...snapOnYaw(point, grid.yaw, gridSnap(grid)), y: grid.y };
}

/** yaw 格子の局所軸。`x` / `z` は水平、`y` は世界鉛直。 */
export type GridAxis = 'x' | 'y' | 'z';

export function gridCoords(point: Vec3, yaw: number): { x: number; y: number; z: number } {
  const local = worldToLocalXZ(point.x, point.z, yaw);
  return { x: local.x, y: point.y, z: local.z };
}

export function fromGridCoords(coords: { x: number; y: number; z: number }, yaw: number): Vec3 {
  const world = localToWorldXZ(coords.x, coords.z, yaw);
  return { x: world.x, y: coords.y, z: world.z };
}

export function gridAxisDirection(axis: GridAxis, yaw: number): Vec3 {
  if (axis === 'y') return { x: 0, y: 1, z: 0 };
  const world = axis === 'x' ? localToWorldXZ(1, 0, yaw) : localToWorldXZ(0, 1, yaw);
  return { x: world.x, y: 0, z: world.z };
}

export function gridLevels(min: number, max: number, snap: number): number[] {
  if (!(snap > 0) || max < min) return [];
  const start = Math.ceil(min / snap - 1e-9);
  const end = Math.floor(max / snap + 1e-9);
  const levels: number[] = [];
  for (let index = start; index <= end; index++) levels.push(index * snap);
  return levels;
}

function lerpVec(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}

/** 線分と格子面の交点。辺が面に乗るときは null。 */
export function intersectSegmentGridPlane(
  a: Vec3,
  b: Vec3,
  axis: GridAxis,
  level: number,
  yaw: number
): Vec3 | null {
  const from = gridCoords(a, yaw)[axis];
  const to = gridCoords(b, yaw)[axis];
  const span = to - from;
  if (Math.abs(span) < 1e-9) return null;
  const t = (level - from) / span;
  if (t < -1e-9 || t > 1 + 1e-9) return null;
  return lerpVec(a, b, Math.min(1, Math.max(0, t)));
}

const GRID_AXES: GridAxis[] = ['x', 'y', 'z'];

function otherGridAxis(axisA: GridAxis, axisB: GridAxis): GridAxis {
  return GRID_AXES.find((axis) => axis !== axisA && axis !== axisB) ?? 'y';
}

/** 2 つの格子面の交線と平面の交点。線が面に平行なら null。 */
export function intersectGridLinePlane(
  axisA: GridAxis,
  levelA: number,
  axisB: GridAxis,
  levelB: number,
  planeOrigin: Vec3,
  planeNormal: Vec3,
  yaw: number
): Vec3 | null {
  if (axisA === axisB) return null;
  const free = otherGridAxis(axisA, axisB);
  const origin = fromGridCoords(
    {
      x: axisA === 'x' ? levelA : axisB === 'x' ? levelB : 0,
      y: axisA === 'y' ? levelA : axisB === 'y' ? levelB : 0,
      z: axisA === 'z' ? levelA : axisB === 'z' ? levelB : 0,
    },
    yaw
  );
  const direction = gridAxisDirection(free, yaw);
  const den =
    direction.x * planeNormal.x + direction.y * planeNormal.y + direction.z * planeNormal.z;
  if (Math.abs(den) < 1e-9) return null;
  const t =
    ((planeOrigin.x - origin.x) * planeNormal.x +
      (planeOrigin.y - origin.y) * planeNormal.y +
      (planeOrigin.z - origin.z) * planeNormal.z) /
    den;
  return {
    x: origin.x + direction.x * t,
    y: origin.y + direction.y * t,
    z: origin.z + direction.z * t,
  };
}

export function normalizeViewGrid(input: unknown): ViewGridState {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return { ...DEFAULT_VIEW_GRID };
  const value = input as Record<string, unknown>;
  const y = typeof value.y === 'number' && Number.isFinite(value.y) ? value.y : DEFAULT_VIEW_GRID.y;
  const yaw =
    typeof value.yaw === 'number' && Number.isFinite(value.yaw) ? value.yaw : DEFAULT_VIEW_GRID.yaw;
  const yawClass = snapYawClass(yaw);
  return {
    y: Math.max(-WORLD_LIMIT, Math.min(WORLD_LIMIT, Math.round(y))),
    yaw: yawClassDegrees(yawClass),
    visible: value.visible !== false,
    snap: normalizeSnap(value.snap),
  };
}
